import CoreBase from '../../../services/engines/CoreBase';
import { BOARD_STATE_COMPLETED, CURRENT_STATE_CAT, DEFAULT_STAGE, DONE_STAGE, SORTS } from './boardConst';

// Pure lane model: Done first, then lane matching (first lane wins), then unmatched lane; sorting, CurrentState warning
class BoardLaneBuilder extends CoreBase {
    _name = 'BoardLaneBuilder';

    /**
     * @param {{lanes: object[]}} board
     * @param {object[]} items - board items
     * @param {{filter: string, sorts: object}} opts
     * @returns {{lanes: object[], unplaced: number, total: number}}
     */
    build = (board, items, opts) => {
        const placed = items.map((item) => ({ item, laneId: this.laneOf(item, board.lanes) }));
        const lanes = board.lanes
            .filter((l) => l.enabled !== false)
            .map((l) => this._lane(l, placed.filter((p) => p.laneId === l.id).map((p) => p.item), opts));
        return { lanes, unplaced: placed.filter((p) => !p.laneId).length, total: items.length };
    };

    /**
     * Done lane for completed/Done items, else first matching lane (board order, enabled or not), else the unmatched lane.
     * @param {object} item
     * @param {object[]} lanes
     * @returns {string|null}
     */
    laneOf = (item, lanes) => this._doneLane(item, lanes) ?? this._matchLane(item, lanes) ?? this._unmatchedLane(lanes);

    // regardless of type/cat
    _doneLane = (item, lanes) => {
        if (!this._isDone(item)) return null;
        return lanes.find((l) => l.dropStage === DONE_STAGE)?.id ?? null;
    };

    _matchLane = (item, lanes) => lanes.find((l) => !l.unmatched && (l.match ?? []).some((r) => this._matches(item, r)))?.id ?? null;

    _unmatchedLane = (lanes) => lanes.find((l) => l.unmatched)?.id ?? null;

    _isDone = (item) => item.stateId === BOARD_STATE_COMPLETED || item.stage === DONE_STAGE;

    /**
     * Open stage lanes on prio, Done and view lanes on created — both descending.
     * @param {object} lane
     * @returns {{key: string, dir: number}}
     */
    defaultSort = (lane) => (lane.dropStage && lane.dropStage !== DONE_STAGE ? { key: 'prioId', dir: -1 } : { key: 'ems', dir: -1 });

    shortDate = (ems) => (ems ? new Date(ems).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' }) : '');

    longDate = (ems) => (ems ? new Date(ems).toLocaleString('nl-NL', { dateStyle: 'medium', timeStyle: 'short' }) : '');

    // AND between filled fields, one value per field is enough
    _matches = (item, rule) => this._field(rule.type, item.type) && this._field(rule.cat, item.cat) && this._field(rule.stage, this._stageOf(item));

    _field = (list, value) => !list?.length || list.includes(value);

    _stageOf = (item) => (item.type === 'Issue' && !item.stage ? DEFAULT_STAGE : item.stage);

    _lane = (lane, cards, opts) => {
        const isStage = !!lane.dropStage;
        const sort = opts.sorts[lane.id] ?? this.defaultSort(lane);
        const visible = cards.filter((c) => !isStage || opts.filter === 'All' || c.cat === opts.filter);
        return {
            id: lane.id,
            label: lane.label,
            dropStage: lane.dropStage ?? null,
            isStage,
            sort,
            sortOptions: isStage ? SORTS.stage : SORTS.view,
            cards: this._sort(visible, sort).map((c) => ({ ...c, dateShort: this.shortDate(c.ems) })),
            warn: this._isStateLane(lane) ? this._stateWarn(visible) : '',
        };
    };

    _sort = (cards, s) => [...cards].sort((a, b) => ((a[s.key] ?? 0) - (b[s.key] ?? 0)) * s.dir);

    _isStateLane = (lane) => (lane.match ?? []).some((r) => r.cat?.includes(CURRENT_STATE_CAT));

    _stateWarn = (cards) => {
        if (!cards.length) return 'No Current State yet';
        return cards.some((c) => /current state/i.test(c.title)) ? '' : 'No Current State Summary Yet';
    };
}

const boardLaneBuilder = new BoardLaneBuilder();
export default boardLaneBuilder;
